import _ from 'lodash';
import moment from 'moment';
import { ActionContext, Module } from 'vuex';
import { AxiosError, AxiosResponse } from 'axios';
import { saveAs } from 'file-saver';
import { successSwal } from '@/helper/swals';
import Charge from '@/types/Charge';
import Impfstoff from '@/types/Impfstoffe';
import Lagerbuchungsart from '@/types/Lagerbuchungsart';
import api from '@/services/Api';
import { LagerfuehrungState, RootState } from '@/store/types';
import Lagerbuchung from '@/types/Lagerbuchung';
import { DataResponse } from '@/types';
import jspm from '@/services/PrintManager';

export type LagerfuehrungFilter = { gueltig: string; };
export type BuchungFilter = { verfuegbar: string };
export type BuchungsprotkollFilter = { von: string; bis: string; alleImpfstellen: boolean };
export type LagerlisteFilter = { verfuegbar: boolean; alleImpfstellen: boolean; preis_muw?:boolean };

export type LoadImpfstoffePayload = { filter: LagerfuehrungFilter };
export type LoadChargenByImpfstoffIdPayload = { filter: BuchungFilter };
export type NeueBuchungPayload = { charge: Charge; buchung: Lagerbuchung };

export type LoadLagerlisteData = DataResponse<{
    [impfstoff_id: number]: {
        impfstoffe_bezeichnung: string;
        impfstoffe_verkaufspreis: number;
        impfstoffe_einkaufspreis: number;
        impfstoffe_preis_mitarbeiter: number;
        ablaufdatum: string;
        lagerbestand: number;
    };
}>;
export type LoadLagerlisteJeChargeData = DataResponse<{
    bezeichnung: string;
    summe: number;
    mindestbestand: number;
    anmerkung: string;
    chargen: Charge[];
}[]>;

export type SetLagerfuehrungDialogs = {
    edit?: boolean;
    buchung?: boolean;
    buchungsprotokoll?: boolean;
    lagerliste?: boolean;
    lagerlisteJeCharge?: boolean;
};

export type SetLagerfuehrungLoaders = {
    impfstoffliste?: boolean;
    chargen?: boolean;
    buchung?: boolean;
    charge?: boolean;
    buchungsprotokoll?: boolean;
    buchungsprotokollExport?: boolean;
    lagerlisteGesamt?: boolean;
};

export type SetLagerfuehrungLagerlisteLoaders = {
    loading?: boolean;
    print?: boolean;
    export?: boolean;
};

export type SetLagerfuehrungLagerlisteJeChargeLoaders = {
    loading?: boolean;
    print?: boolean;
    export?: boolean;
};

type Context = ActionContext<LagerfuehrungState, RootState>;

export const lagerfuehrung: Module<LagerfuehrungState, RootState> = {
    namespaced: true,
    state: {
        dialogs: {
            edit: false,
            buchung: false,
            buchungsprotokoll: false,
            lagerliste: false,
            lagerlisteJeCharge: false,
        },
        loaders: {
            impfstoffliste: false,
            chargen: false,
            buchung: false,
            charge: false,
            buchungsprotokoll: false,
            buchungsprotokollExport: false,
            lagerliste: {
                loading: false,
                print: false,
                export: false,
            },
            lagerlisteJeCharge: {
                loading: false,
                print: false,
                export: false,
            },
            lagerlisteGesamt: false,
        },
        impfstoffliste: [],
        impfstoffchargen: [],
        //old
        selected_impfstoff: null,
        impfstoffe: [],
        chargen: [],
        lagerbuchungsarten: [],
        lagerbuchungen: [],
        lagerliste: [],
        lagerlisteJeCharge: [],
    },
    actions: {
        loadImpfstoffe({ commit, rootState }: Context, payload?: LoadImpfstoffePayload): void {
            commit('setLoadersImpfstoffliste', true);

            const data = {
                impfstelle_id: rootState.authentication.impfstelle_id,
                ...payload,
            };

            api.post('lager/lagerfuehrung/load', data)
                .then((response: AxiosResponse) => commit('setImpfstoffliste', response.data.body))
                .catch(console.error)
                .finally(() => commit('setLoadersImpfstoffliste', false));
        },
        loadLagerbuchungsarten({ commit }: Context): void {
            api.post('lager/lagerbuchungsarten/load')
                .then((response: AxiosResponse) => commit('setLagerbuchungsarten', response.data.body))
                .catch(console.error);
        },
        loadChargenByImpfstoffId({ commit, state, rootState }: Context,
                                 payload?: LoadChargenByImpfstoffIdPayload): void {
            commit('setLoadersChargen', true);
            commit('setChargenByImpfstoff', []);

            const data = {
                impfstelle_id: rootState.authentication.impfstelle_id,
                impfstoff_id: state.selected_impfstoff?.impfstoff_id,
                ...payload,
            };

            api.post('lager/lagerfuehrung/chargen', data)
                .then((response: AxiosResponse) => commit('setChargenByImpfstoff', response.data.body))
                .catch(console.error)
                .finally(() => commit('setLoadersChargen', false));
        },
        neueBuchung({ commit }: Context, payload: NeueBuchungPayload): Promise<boolean> {
            commit('setLoadersBuchung', true);

            return api.post('lager/lagerbuchungen', payload)
                .then(() => {
                    commit('setLoadersBuchung', false);
                    successSwal(payload.buchung.lagerbuchungsart ?? 'Lagerbuchung', 'gebucht');
                    return true;
                })
                .catch((error: AxiosError) => {
                    console.error(error);
                    commit('setLoadersBuchung', false);
                    return false;
                });
        },
        updateCharge({ commit }: Context, payload: Charge): Promise<boolean> {
            commit('setLoadersCharge', true);

            return api.post('lager/lagerbuchungen/editcharge', payload)
                .then(() => {
                    commit('setLoadersCharge', false);
                    successSwal(payload.chargennr as string, 'gespeichert');
                    return true;
                })
                .catch((error: AxiosError) => {
                    console.log(error);
                    commit('setLoadersCharge', false);
                    return false;
                });
        },
        setImpfstoff({ commit }: Context, payload: Impfstoff | null): void {
            commit('setImpfstoff', payload);
        },
        getLagerbuchungen({ commit, state, rootState }: Context, payload?: BuchungsprotkollFilter): void {
            commit('setLoadersBuchungsprotokoll', true);
            commit('setLagerbuchungen', []);

            const data = {
                impfstoff_id: state.selected_impfstoff?.impfstoff_id,
                impfstelle_id: rootState.authentication.impfstelle_id,
                alleImpfstellen: false,
                ...payload,
            };

            api.post('lager/lagerfuehrung/get', data)
                .then((response: AxiosResponse) => commit('setLagerbuchungen', response.data.body))
                .catch(console.error)
                .finally(() => commit('setLoadersBuchungsprotokoll', false));
        },
        exportLagerbuchungen({ commit, state, rootState, rootGetters }:
                                 Context, payload?: BuchungsprotkollFilter): void {
            commit('setLoadersBuchungsprotokollExport', true);

            const data = {
                impfstoff_id: state.selected_impfstoff?.impfstoff_id,
                impfstelle_id: rootState.authentication.impfstelle_id,
                alleImpfstellen: false,
                ...payload,
            };

            api.post('lager/lagerfuehrung/export', data, { responseType: 'blob' })
                .then((response: AxiosResponse) => {
                    const date = moment().format('DD-MM-YYYY_HH-mm-ss');
                    const impfstelle = !data.alleImpfstellen
                        ? `${rootGetters['authentication/getImpfstelleName'].replace(/ /g, '_')}_`
                        : '';
                    saveAs(response.data, `Buchungsprotokoll_${impfstelle}${date}.xlsx`);
                })
                .catch(console.error)
                .finally(() => commit('setLoadersBuchungsprotokollExport', false));
        },
        loadLagerliste({ commit, rootState }: Context, payload?: LagerfuehrungFilter): void {
            commit('setLoadersLagerliste', { loading: true });

            const data = {
                gueltig: 'j',
                verfuegbar: true,
                alleImpfstellen: false,
                impfstelle_id: rootState.authentication.impfstelle_id,
                ...payload,
            };

            api.post('lager/lagerfuehrung/lagerliste', data)
                .then((response: AxiosResponse) => commit('setLagerliste', response.data))
                .catch(console.error)
                .finally(() => commit('setLoadersLagerliste', { loading: false }));
        },
        loadLagerlisteJeCharge({ commit, rootState }: Context, payload?: LagerfuehrungFilter): void {
            commit('setLoadersLagerlisteJeCharge', { loading: true });

            const data = {
                gueltig: 'j',
                verfuegbar: true,
                alleImpfstellen: false,
                impfstelle_id: rootState.authentication.impfstelle_id,
                ...payload,
            };

            api.post('lager/lagerfuehrung/lagerlistecharge', data)
                .then((response: AxiosResponse) => commit('setLagerlistecharge', response.data))
                .catch(console.error)
                .finally(() => commit('setLoadersLagerlisteJeCharge', { loading: false }));
        },
        printLagerliste({ commit, dispatch, rootState }: Context,
                        payload?: LagerfuehrungFilter & LagerlisteFilter): void {
            commit('setLoadersLagerliste', { print: true });

            const data = {
                gueltig: 'j',
                verfuegbar: true,
                alleImpfstellen: false,
                impfstelle_id: rootState.authentication.impfstelle_id,
                ...payload,
            };

            api.post('lager/lagerlisten/index', data, { responseType: 'blob' })
                .then(({ data }: AxiosResponse) => dispatch('globals/printDokument', { data }, { root: true }))
                .catch(console.error)
                .finally(() => commit('setLoadersLagerliste', { print: false }));
        },
        exportLagerliste({ commit, rootState, rootGetters }: Context,
                         payload?: LagerfuehrungFilter & LagerlisteFilter): void {
            commit('setLoadersLagerliste', { export: true });

            const data = {
                gueltig: 'j',
                verfuegbar: true,
                alleImpfstellen: false,
                impfstelle_id: rootState.authentication.impfstelle_id,
                ...payload,
            };

            api.post('lager/lagerlisten/export', data, { responseType: 'blob' })
                .then((response: AxiosResponse) => {
                    const date = moment().format('DD-MM-YYYY_HH-mm-ss');
                    const impfstelle = !data.alleImpfstellen
                        ? `${rootGetters['authentication/getImpfstelleName'].replace(/ /g, '_')}_`
                        : '';
                    saveAs(response.data, `Lagerliste_${impfstelle}${date}.xlsx`);
                })
                .catch(console.error)
                .finally(() => commit('setLoadersLagerliste', { export: false }));
        },
        printLagerlisteJeCharge({ commit, dispatch, rootState }: Context,
                                payload?: LagerfuehrungFilter & LagerlisteFilter): void {
            commit('setLoadersLagerlisteJeCharge', { print: true });

            const data = {
                gueltig: 'j',
                verfuegbar: true,
                alleImpfstellen: false,
                impfstelle_id: rootState.authentication.impfstelle_id,
                ...payload,
            };

            api.post('lager/lagerlistecharge/index', data, { responseType: 'blob' })
                .then(({ data }: AxiosResponse) => {
                    const printRotation = jspm.printer?.manager?.PrintRotation?.Rot90;
                    const paperName = 'A4';
                    dispatch('globals/printDokument', { data, printRotation, paperName }, { root: true });
                })
                .catch(console.error)
                .finally(() => commit('setLoadersLagerlisteJeCharge', { print: false }));
        },
        exportLagerlisteJeCharge({ commit, rootState, rootGetters }: Context,
                                 payload?: LagerfuehrungFilter & LagerlisteFilter): void {
            commit('setLoadersLagerlisteJeCharge', { export: true });

            const data = {
                gueltig: 'j',
                verfuegbar: true,
                alleImpfstellen: false,
                impfstelle_id: rootState.authentication.impfstelle_id,
                ...payload,
            };

            api.post('lager/lagerlistecharge/export', data, { responseType: 'blob' })
                .then((response: AxiosResponse) => {
                    const date = moment().format('DD-MM-YYYY_HH-mm-ss');
                    const impfstelle = !data.alleImpfstellen
                        ? `${rootGetters['authentication/getImpfstelleName'].replace(/ /g, '_')}_`
                        : '';
                    saveAs(response.data, `LagerlisteJeCharge_${impfstelle}${date}.xlsx`);
                })
                .catch(console.error)
                .finally(() => commit('setLoadersLagerlisteJeCharge', { export: false }));
        },
        exportLagerlisteGesamt({ commit, rootState }: Context,
                               payload?: LagerfuehrungFilter & LagerlisteFilter): void {
            commit('setLoaders', { lagerlisteGesamt: true });

            const data = {
                gueltig: 'j',
                verfuegbar: true,
                alleImpfstellen: true,
                gesamt: true,
                impfstelle_id: rootState.authentication.impfstelle_id,
                ...payload,
            };

            api.post('lager/lagerlisten/export', data, { responseType: 'blob' })
                .then((response: AxiosResponse) => {
                    const date = moment().format('DD-MM-YYYY_HH-mm-ss');
                    saveAs(response.data, `LagerlisteGesamt_${date}.xlsx`);
                })
                .catch(console.error)
                .finally(() => commit('setLoaders', { lagerlisteGesamt: false }));
        },
        setDialogs({ commit }: Context, payload: SetLagerfuehrungDialogs): void {
            commit('setDialogs', payload);
        },
        setLoaders({ commit }: Context, payload: SetLagerfuehrungLoaders): void {
            commit('setLoaders', payload);
        },
        setLoadersLagerliste({ commit }: Context, payload: SetLagerfuehrungLagerlisteLoaders): void {
            commit('setLoadersLagerliste', payload);
        },
        setLoadersLagerlisteJeCharge({ commit }: Context, payload: SetLagerfuehrungLagerlisteJeChargeLoaders): void {
            commit('setLoadersLagerlisteJeCharge', payload);
        },
    },
    mutations: {
        setDialogs(state: LagerfuehrungState, payload: SetLagerfuehrungDialogs): void {
            state.dialogs = {
                ...state.dialogs,
                ...payload,
            };
        },
        setLoaders(state: LagerfuehrungState, payload: SetLagerfuehrungLoaders): void {
            state.loaders = {
                ...state.loaders,
                ...payload,
            };
        },
        setLoadersLagerliste(state: LagerfuehrungState, payload: SetLagerfuehrungLagerlisteLoaders): void {
            state.loaders.lagerliste = {
                ...state.loaders.lagerliste,
                ...payload,
            };
        },
        setLoadersLagerlisteJeCharge(state: LagerfuehrungState,
                                     payload: SetLagerfuehrungLagerlisteJeChargeLoaders): void {
            state.loaders.lagerlisteJeCharge = {
                ...state.loaders.lagerlisteJeCharge,
                ...payload,
            };
        },
        setLoadersImpfstoffliste(state: LagerfuehrungState, visible: boolean): void {
            state.loaders.impfstoffliste = visible;
        },
        setImpfstoffliste(state: LagerfuehrungState, payload: Partial<Impfstoff>[]): void {
            state.impfstoffliste = payload.map((data) => new Impfstoff(data));
        },
        setLoadersChargen(state: LagerfuehrungState, visible: boolean): void {
            state.loaders.chargen = visible;
        },
        setChargenByImpfstoff(state: LagerfuehrungState, payload: Partial<Charge>[]): void {
            state.impfstoffchargen = payload.map((data) => new Charge(data));
        },
        //old
        setCharge(state: LagerfuehrungState, payload: Partial<Charge>[]): void {
            state.chargen = payload.map((data) => new Charge(data));
        },
        setImpfstoffe(state: LagerfuehrungState, payload: Partial<Impfstoff>[]): void {
            state.impfstoffe = payload.map((data) => new Impfstoff(data));
        },
        setImpfstoff(state: LagerfuehrungState, impfstoff: Impfstoff | null): void {
            state.selected_impfstoff = impfstoff ? new Impfstoff(impfstoff) : null;
        },
        setLagerbuchungsarten(state: LagerfuehrungState, payload: Partial<Lagerbuchungsart>[]): void {
            state.lagerbuchungsarten = payload.map((data) => new Lagerbuchungsart(data));
        },
        setDialogEdit(state: LagerfuehrungState, visible: boolean): void {
            state.dialogs.edit = visible;
        },
        setLagerbuchungen(state: LagerfuehrungState, payload: Partial<Lagerbuchung>[]): void {
            state.lagerbuchungen = payload.map((data) => new Lagerbuchung(data));
        },
        setLoadersBuchung(state: LagerfuehrungState, visible: boolean): void {
            state.loaders.buchung = visible;
        },
        setLoadersCharge(state: LagerfuehrungState, visible: boolean): void {
            state.loaders.charge = visible;
        },
        setLoadersBuchungsprotokoll(state: LagerfuehrungState, visible: boolean): void {
            state.loaders.buchungsprotokoll = visible;
        },
        setLoadersBuchungsprotokollExport(state: LagerfuehrungState, visible: boolean): void {
            state.loaders.buchungsprotokollExport = visible;
        },
        setLagerliste(state: LagerfuehrungState, { body }: LoadLagerlisteData): void {
            state.lagerliste = _
                .chain(body)
                .groupBy('impfstoff_id')
                .map((chargen) => {
                    const [firstCharge] = chargen;
                    return {
                        impfstoffe_bezeichnung: firstCharge?.impfstoffe_bezeichnung,
                        impfstoffe_verkaufspreis: firstCharge?.impfstoffe_verkaufspreis,
                        impfstoffe_einkaufspreis: firstCharge?.impfstoffe_einkaufspreis,
                        impfstoffe_preis_mitarbeiter: firstCharge?.impfstoffe_preis_mitarbeiter,
                        ablaufdatum: _.minBy(chargen ?? [], 'ablaufdatum')?.ablaufdatum ?? '',
                        lagerbestand: _.sumBy(chargen ?? [], 'lagerbestand'),
                    };
                })
                .sortBy([(charge): string => charge.impfstoffe_bezeichnung.toUpperCase()])
                .value();
        },
        setLagerlistecharge(state: LagerfuehrungState, { body }: LoadLagerlisteJeChargeData): void {
            state.lagerlisteJeCharge = _
                .chain(body)
                .map((impfstoff) => {
                    impfstoff.chargen = _
                        .chain(impfstoff.chargen)
                        .map((data) => new Charge(data))
                        .sortBy(impfstoff.chargen, 'ablaufdatum')
                        .value();

                    return impfstoff;
                })
                .sortBy([(impfstoff): string => impfstoff.bezeichnung.toUpperCase()])
                .value();
        },
    },
    getters: {
        getImpfstoff_id(state: LagerfuehrungState): number {
            return state.selected_impfstoff?.impfstoff_id ?? -1;
        },
        getChargenByImpfstoffId(state: LagerfuehrungState): Charge[] {
            return state.chargen.filter((charge) => {
                return charge.impfstoff_id === state.selected_impfstoff?.impfstoff_id;
            });
        },
        lagerbuchungsarten(state: LagerfuehrungState): Lagerbuchungsart[] {
            return state.lagerbuchungsarten.filter((lagerbuchungsart) => {
                return lagerbuchungsart.lagerbuchungsart !== 'Storno';
            });
        },
    },
};
